<template>
  <v-dialog class="modal" v-model="internal" max-width="950" content-class="upload-csv-dialog">
    <v-card class="pa-5 upload-csv-card">
      <div class="d-flex justify-space-between align-center">
        <v-card-title class="strong">{{ $t('uploadCsvModal.title', { step: steps }) }}
        </v-card-title>
        <v-btn icon @click="resetForm"><v-icon small>mdi-close</v-icon></v-btn>
      </div>
      <v-progress-linear :value="(steps / 3) * 100" rounded height="10" color="yellow"></v-progress-linear>
      <v-card-text class="upload-csv-card__body pa-0">
      <v-expand-transition>
      <div v-if="steps === 1">
        <v-card-actions class="d-flex my-1 align-center justify-center">
          <div class="text-center">
            <h4>{{ $t('uploadCsvModal.selectCsvFile') }}</h4>
            <h5>{{ $t('uploadCsvModal.uploadCsvHint') }}</h5>
          </div>
        </v-card-actions>
        <v-divider></v-divider>
        <!--BOX UPLOAD-->
        <v-card-text>
          <div class="upload-files" @click="$refs.fileInput.click()">
            <div class="my-3" :class="itemsUploaded
              ? 'background-icon-uploaded'
              : 'background-icon-upload'
              ">
              <v-icon color="#ffbf00" v-if="!itemsUploaded" large>mdi-upload</v-icon>
              <v-icon color="#26c281" large v-else>mdi-file-outline</v-icon>
            </div>

            <h3>{{ $t('importPortModal.clickToSelect') }}</h3>
            <h5 v-if="!itemsUploaded">
              {{ $t('uploadCsvModal.csvFileHint') }}
            </h5>
            <h5 v-else>{{ formattedSize }}</h5>

            <div class="mt-7 d-flex align-center justify-center">
              <base-button isActive class="mr-2" @click.stop="$refs.fileInput.click()">
                {{ $t('importPortModal.selectFile') }}
              </base-button>
              <input type="file" ref="fileInput" style="display: none" accept=".csv, .xlsx" @change="handleFile" />

              <div class="file-name">{{ selectedFile.name }}</div>
            </div>
          </div>
        </v-card-text>
      </div>

      <!--input here-->
      <div v-else-if="steps === 2" >
        <!--check if it was filled-->
        <v-card style="background-color:#1e2b23; border:1px solid #3dbb65; color:#9efba3;" outlined
          class="elevation-0 my-4">
          <div v-if="autoFillKey" class="d-flex flex-row justify-start items-center pa-2">
            <v-icon>mdi-check-circle</v-icon>
            <h5 class="mx-5">{{ $t('uploadCsvModal.autoFillSuccess') }}</h5>
          </div>
          <div v-else class="d-flex flex-row justify-start items-center pa-2">
            <v-icon color="red">mdi-alert-circle-outline</v-icon>
            <h5 class="mx-5">{{ $t('uploadCsvModal.autoFillError') }}</h5>
          </div>
        </v-card>
        <v-alert v-if="erroLayout" dense outlined type="error" class="mt-2 mb-0">
          {{ $t('uploadCsvModal.' + erroLayout) }}
        </v-alert>
        <v-alert v-else-if="isSpotLayout" dense outlined type="info" class="mt-2 mb-0">
          {{ $t('uploadCsvModal.spotLayoutDetected') }}
        </v-alert>
        <v-expansion-panels v-else-if="!erroLayout" v-model="stepTwoPanels" outlined multiple flat class="d-flex flex-column flex-gap-2 mt-2 px-0">
          <v-expansion-panel class="expansion-panel pb-0">
            <v-expansion-panel-header>
              {{ $t('uploadCsvModal.requiredFields') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-form ref="form">
                <v-card class="pt-2 px-4 position-relative elevation-0 pb-0" style="border:none">
                  <v-chip color="red" outlined>{{ $t('instructions.uploadFiles.obligatory') }}</v-chip>
                  <div v-for="(header, idx) in headerItems" :key="idx">
                    <div class="d-flex flex-row justify-space-between align-center my-1"
                      v-for="(title, index) in header.ObligatoryTitle" :key="index">
                      <div class="d-flex flex-column">
                        <h4>{{ title }}</h4>
                        <p>{{ header.obligatorySubtitle[index] }}</p>
                      </div>

                      <v-select :items="requiredItems" v-model="selectedInput.obligatory[index]" outlined
                        :rules="[requiredRules]" dense style="max-width: 200px" />
                    </div>
                  </div>
                </v-card>
              </v-form>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel class="expansion-panel">
            <v-expansion-panel-header>
              {{ $t('uploadCsvModal.optionalFields') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="pt-4 px-4">
                <div v-for="(header, idx) in headerItems" :key="idx">
                  <div class="d-flex flex-row justify-space-between align-center my-1"
                    v-for="(title, index) in header.OptionalTitle" :key="index">
                    <div class="d-flex flex-column">
                      <h4>{{ title }}</h4>
                      <p>{{ header.optionalSubtitle[index] }}</p>
                    </div>

                    <v-select :items="requiredItems" v-model="selectedInput.optional[index]" outlined dense
                      style="max-width: 200px" />
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <!--input end-->

      <div v-else>
        <!--overview file-->
        <div class="mt-3 mb-1 mx-auto d-flex flex-column align-center">
          <v-icon color="#26c281" large class="background-icon-uploaded">mdi-check-circle-outline</v-icon>
          <h3>{{ $t('uploadCsvModal.confirmImport') }}</h3>
          <p>{{ $t('uploadCsvModal.reviewBeforeImport') }}</p>
        </div>
        <v-card
          outlined
          class="mt-4 elevation-0 pr-4 py-2 d-flex flex-row justify-space-between align-center"
        >
          <div>
            <v-card-title>
              <v-icon>mdi-file-document-outline</v-icon>
              {{ selectedFile.name }}</v-card-title>
            <v-card-subtitle>{{ jsonFile.lines }} {{ $t('uploadCsvModal.registered') }} • {{ formattedSize }}
            </v-card-subtitle>
          </div>
          <div>
            <v-chip>{{ mappedFieldCount }} {{ $t('uploadCsvModal.fieldsMapped') }}</v-chip>
          </div>
        </v-card>

        <v-expansion-panels v-model="stepThreePanels" outlined multiple flat class="mt-1">
          <v-expansion-panel class="expansion-panel my-2">
            <v-expansion-panel-header>
              {{ $t('uploadCsvModal.mappedFields') }}
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <div v-for="(item, idx) in headerItems" :key="idx" class="mapped-fields">
                <div v-for="(obligatory, obIndex) in item.ObligatoryTitle" :key="'ob' + obIndex" class="field-row">
                  <p>{{ obligatory }} -</p>
                  <h4>{{ selectedInput.obligatory[obIndex] }}</h4>
                </div>

                <div v-for="(optional, opIndex) in item.OptionalTitle" :key="'op' + opIndex" class="field-row">
                  <p>{{ optional }} -</p>
                  <h4>{{ selectedInput.optional[opIndex] }}</h4>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel class="expansion-panel my-2">
            <v-expansion-panel-header>
              {{ $t('uploadCsvModal.dataPreview') }}
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <base-data-table hide-default-footer :headers="tableHeaders" :items="tableOutput">
                <template v-slot:item.maturitydate="{ item }">
                  <div class="text-no-wrap font-weight-bold">{{ formatShortWeekdayDate(item.maturitydate).date }}</div>
                  <div class="text-label-small">{{ formatShortWeekdayDate(item.maturitydate).time }}</div>
                </template>

                <template v-slot:item.side="{ item }">
                  <div class="text-no-wrap font-weight-bold" :class="item.side === 'BUY' ? 'buy-color' : 'sell-color'">
                    {{ item.side }}</div>
                </template>
              </base-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- snackbar here-->
        <v-snackbar v-model="autoFillKey">{{ selectedFile.lines }} {{ $t('uploadCsvModal.linesLoaded') }}</v-snackbar>
      </div>
      </v-expand-transition>
      </v-card-text>

      <v-card-actions class="cta-buttons d-flex justify-space-between">
        <base-button :isDisable="steps === 1" @click="steps--"><v-icon>mdi-chevron-left</v-icon> {{
          $t('instructions.back')
        }}</base-button>

        <div class="d-flex flex-gap-2">
          <!-- <base-button @click="resetForm">{{ $t('instructions.cancel') }}</base-button> -->
          <base-button @click="nextPages" isActive :isDisable="!itemsUploaded">
            {{ steps === 3 ? $t('uploadCsvModal.finish') : $t('instructions.next') }}
            <v-icon>mdi-chevron-right</v-icon>
          </base-button>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { useAnalyticsProStore } from "@/store/analyticsStore";
import { mapStores } from "pinia";
import { formatShortWeekdayDate } from "@/utils/utils";
import { isSpotLayout } from "@/utils/flowSightReportLayout";
import { SPOT } from "@/utils/flowSightProducts";

export default {
  name: "UploadModal",
  data() {
    return {
      steps: 1,
      itemsUploaded: false,
      selectedFile: {
        name: "",
        size: "",
        lines: 0,
      },
      jsonFile: null,
      emittedData: [],

      requiredItems: [],
      // selectedInput: {
      //   obligatory: Array(5).fill(null),
      //   optional: Array(8).fill(null),
      // },
      stepThreePanels: [],
      stepTwoPanels: [0],
      autoFillKey: null,
      // O arquivo casa com o produto escolhido? Null = sim; senao guarda a chave
      // i18n do erro. Ver o comentario em analyticsStore.dataEmitter.
      arquivoEhSpot: false,
      erroLayout: null,
    };
  },
  watch: {
    internal(val) {
      if (!val) {
        this.resetForm();
      }
    },
    //   autoFillKey(newVal){
    //   if(newVal && this.analytics)
    //   {
    //     this.wasAutoFilled = true;
    //     this.formData[newVal] = this.analytics[0][this.jsonFile.field.indexOf(newVal)]
    //   }
    // }
  },
  computed: {
    ...mapStores(useAnalyticsProStore),
    analytics() {
      return this.analyticsProStore.getAnalytics;
    },
    // Colunas do preview no produto SPOT. Nao dava para derivar de headerItems:
    // aquele e o mapeamento do relatorio de NDF, e o caminho de spot nem passa
    // por ele - por isso o preview aparecia com "Maturity Date" vazia e "FWD",
    // que naquele relatorio nao existem. Aqui as colunas dizem o que o dado e:
    // liquidacao no lugar do vencimento, e as duas pontas da taxa a vista.
    colunasSpot() {
      const t = (k) => this.$t('uploadCsvModal.spotColumns.' + k)
      return [
        { text: t('settlement'), value: 'settlement' },
        { text: t('status'), value: 'status' },
        { text: t('notional'), value: 'notional' },
        { text: t('side'), value: 'side' },
        { text: t('counterparty'), value: 'counterparty' },
        { text: t('currency'), value: 'currency' },
        { text: t('costRate'), value: 'fwd' },
        { text: t('clientRate'), value: 'fwdclient' },
        { text: t('channel'), value: 'channel' },
      ]
    },
    tableHeaders() {
      if (this.isSpotLayout) {
        const largura = `${100 / this.colunasSpot.length}%`
        return this.colunasSpot.map((c) => ({
          ...c,
          sortable: false,
          align: 'center',
          width: largura,
          class: 'responsive-table-header text-no-wrap',
          cellClass: 'responsive-table-cell',
        }))
      }

      const excludedHeaders = new Set([
        "fwd-client",
        "yield(ccy)",
        "yield(client)",
        "rfqtimestamp"
      ]);

      const titles = [
        ...this.headerItems[0].ObligatoryTitle,
        ...this.headerItems[0].OptionalTitle,
      ].filter((t) => {
        const normalized = String(t).toLowerCase().replace(/\s+/g, "");
        return !excludedHeaders.has(normalized);
      });

      const total = titles.length - 1 || 1;
      const dynamicWidth = `${100 / total}%`;

      return titles.map((t) => {
        const key = String(t).toLowerCase().replace(/\s+/g, "");

        return {
          text: t,
          value: key,
          sortable: false,
          align: "center",
          width: dynamicWidth,
          class: "responsive-table-header text-no-wrap",
          cellClass: "responsive-table-cell",
        };
      });
    },
    tableOutput() {
      return this.analytics.slice(0, 3)
    },
    mappedFieldCount() {
      const obligatory = this.selectedInput?.obligatory.filter(
        (v) => v !== null && v !== ""
      ).length;
      const optional = this.selectedInput?.optional.filter(
        (v) => v !== null && v !== ""
      ).length;

      return obligatory + optional;
    },
    internal: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    formattedSize() {
      if (this.selectedFile.size < 1024) return this.selectedFile.size + " B";
      if (this.selectedFile.size < 1024 * 1024)
        return (this.selectedFile.size / 1024).toFixed(2) + " KB";
      if (this.selectedFile.size < 1024 * 1024 * 1024)
        return (this.selectedFile.size / (1024 * 1024)).toFixed(2) + " MB";
      return (this.selectedFile.size / (1024 * 1024 * 1024)).toFixed(2) + " GB";
    },
    headerItems() {
      return this.analyticsProStore.headerItems
    },
    isSpotLayout() {
      return this.analyticsProStore.flowSightProduct === SPOT;
    },
    selectedInput:
    {
      get() {
        return this.analyticsProStore.selectedInput

      },
      set(v) {
        this.analyticsProStore.selectedInput = v
      }
    }
  },
  props: {
    value: { type: Boolean, default: false },
  },
  methods: {
    handleFile(e) {
      const file = e.target.files[0];
      const maxSize = 7 * 1024 * 1024;
      if (!file || file.length > maxSize) return; //too large or doesnt exist

      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        this.jsonFile = await this.analyticsProStore.csvToJson(text); //json file from csv
        this.requiredItems = this.jsonFile.field;
        this.selectedFile.lines = this.jsonFile.lines

        //auto fill
        this.autoFill()
      };
      reader.readAsText(file);

      this.selectedFile.name = file.name;
      this.selectedFile.size = file.size;
      this.itemsUploaded = true;
    },
    formatShortWeekdayDate(item) {
      return formatShortWeekdayDate(item) //from utils
    },
    autoFill() {
      this.arquivoEhSpot = isSpotLayout(this.jsonFile.field)
      // O arquivo tem que casar com o produto ESCOLHIDO. Nao ha mais troca
      // automatica de produto - ver o comentario em analyticsStore.dataEmitter.
      this.erroLayout = this.isSpotLayout
        ? (this.arquivoEhSpot ? null : 'spotExpected')
        : (this.arquivoEhSpot ? 'ndfExpected' : null)
      if (this.erroLayout) { this.autoFillKey = false; return }
      if (this.isSpotLayout) {
        // Nada a casar: o mapeamento e fixo. Sinaliza sucesso para o passo
        // seguinte nao acusar "nao consegui preencher".
        this.autoFillKey = true
        return
      }
      const result = this.analyticsProStore.matchAutoKeyToLabel(this.jsonFile)
      this.selectedInput = {
        ...this.selectedInput,
        obligatory: result.obligatory,
        optional: result.optional
      }

      this.autoFillKey = result.isValid
    },
    nextPages() {
      this.steps++;
      // O formulario de colunas so vale para o relatorio de NDF. No de spot ele
      // barraria o upload pedindo "Maturity Date", que aquele relatorio nao tem.
      // Arquivo fora do formato do produto escolhido nao avanca de jeito nenhum.
      if (this.erroLayout) { this.steps = 2; return; }
      if (this.steps == 2 && !this.isSpotLayout) {
        const valid = this.$refs.form?.validate();
        if (!valid) return;
      }
      if (!this.isSpotLayout && !this.selectedInput.obligatory.length) return;

      if (this.steps == 3) {
        if (this.selectedInput) {
          const r = this.analyticsProStore.dataEmitter(this.jsonFile, this.selectedInput, this.selectedFile.name);
          // Ultima linha de defesa: se o store recusou o arquivo, nao adianta
          // seguir para a tela de confirmacao com o dataset antigo na memoria.
          if (r && r.ok === false) { this.erroLayout = r.reason; this.steps = 2; return; }
          // localStorage.setItem("analyticsProData", JSON.stringify(this.analytics));
        }
      }
      if (this.steps > 3) {
        this.resetForm();
      }
    },
    resetForm() {
      //reset
      this.steps = 1;
      this.itemsUploaded = false;
      this.selectedInput = {
        obligatory: Array(5).fill(null),
        optional: Array(8).fill(null),
      };
      this.arquivoEhSpot = false;
      this.erroLayout = null;
      this.selectedFile = { name: "", size: "", lines: 0 };
      this.emittedData = [];
      this.$emit("input", false);
    },

    requiredRules(v) {
      return !!v || this.$t('uploadCsvModal.requiredItem');
    },
  },

};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Albert+Sans:ital,wght@0,100..900;1,100..900&display=swap");


.upload-files {
  cursor: pointer;
  border: 1px dashed rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  width: 100%;
  padding-top: 3rem;
  padding-bottom: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.background-icon-uploaded {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  background: rgba(38, 194, 129, 0.1);
  width: 55px;
  height: 55px;
  margin: auto;
  border-radius: 999px !important;
}

.buy-color {
  color: #27c1e6;
}

.sell-color {
  color: #ffb300;
}

.expansion-panel {
  background: var(--surface) !important;
  border: thin solid rgba(255, 255, 255, 0.12) !important;
}

.background-icon-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  background: rgba(255, 191, 0, 0.1);
  width: 55px;
  height: 55px;
  margin: auto;
  border-radius: 999px !important;
}

.v-card {
  overflow-x: hidden;
}

.upload-csv-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

::v-deep .v-expansion-panel-content__wrap{
  padding-bottom: 0 !important;
}

.cta-buttons {
  flex: 0 0 auto;
  margin-top: 1rem;
}

.upload-files h5 {
  font-weight: 400 !important;
}

.mapped-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: flex-start;
  justify-content: flex-start;
}

.field-row {
  display: flex;
}
</style>
